import { ReactNode } from "react";
import classNames from "classnames";

export type BadgeColor = `blue` | `green` | `grey` | `orange` | `red`;
export type BadgeSize = `sm` | `md` | `lg`;

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
    children: ReactNode;
    color?: BadgeColor;
    size?: BadgeSize;
    icon?: ReactNode;
}

const colorClasses: Record<BadgeColor, string> = {
    blue: `bg-blue-100 text-blue-800`,
    green: `bg-green-100 text-green-700`,
    grey: `bg-neutral-100 text-neutral-700`,
    orange: `bg-orange-100 text-orange-700`,
    red: `bg-red-100 text-red-700`
};

// Padding and gap differ between icon and no-icon variants per size.
// Without icon: symmetric px. With icon: reduced left padding + gap.
const sizeClasses: Record<BadgeSize, { base: string; noIcon: string; withIcon: string }> = {
    sm: { base: `py-0.5 text-xs leading-4`,  noIcon: `px-1`,   withIcon: `pl-[3px] pr-1 gap-0.5` },
    md: { base: `py-1 text-xs leading-4`,    noIcon: `px-1.5`, withIcon: `pl-1 pr-1.5 gap-1`     },
    lg: { base: `py-1 text-sm leading-5`,    noIcon: `px-2`,   withIcon: `pl-1.5 pr-2 gap-1.5`   }
};

export const Badge = ({
    children,
    color = `grey`,
    size = `md`,
    icon,
    className,
    ...rest
}: BadgeProps) => (
    <span
        className={classNames(
            `inline-flex items-center justify-center rounded font-mono`,
            colorClasses[color],
            sizeClasses[size].base,
            icon ? sizeClasses[size].withIcon : sizeClasses[size].noIcon,
            className
        )}
        {...rest}
    >
        {icon}
        {children}
    </span>
);
